/**
 * Wraps fetch with retry + exponential backoff for Cloudflare errors
 * that occur during deploys (origin server temporarily unavailable).
 */

/** Cloudflare status codes that indicate a transient deploy. */
const RETRYABLE_STATUS_CODES = new Set([
  502, // Bad Gateway
  503, // Service Unavailable
  520, // Unknown Error
  521, // Web Server Is Down
  522, // Connection Timed Out
  523, // Origin Is Unreachable
  524, // A Timeout Occurred
]);

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 30_000;

export async function retryFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const response = await fetch(input, init);

    if (!RETRYABLE_STATUS_CODES.has(response.status)) {
      return response;
    }

    lastResponse = response;

    if (attempt < MAX_RETRIES) {
      // Abort signal already fired — no point retrying.
      if (init?.signal?.aborted) return response;

      const delay = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
      const jitter = delay * (0.5 + Math.random() * 0.5);
      console.warn(
        `[retryFetch] Got ${response.status}, retrying in ${Math.round(jitter)}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
      );
      await new Promise((resolve) => setTimeout(resolve, jitter));
    }
  }

  // All retries exhausted — return the last response so the caller sees the error.
  return lastResponse!;
}

/** Returns true if the string looks like an HTML page (e.g. Cloudflare error). */
export function looksLikeHtml(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith("<!") || trimmed.startsWith("<html");
}

const DEPLOY_ERROR_MESSAGE =
  "The server is restarting (likely a deploy in progress). Hit Retry to re-establish the session.";

/** Sanitize an error message that may contain a raw Cloudflare HTML error page. */
export function sanitizeErrorMessage(message: string): string {
  if (looksLikeHtml(message)) return DEPLOY_ERROR_MESSAGE;
  return message;
}
